import type { ReactNode } from 'react';

/**
 * Admin surface navigation registry — same pattern as the console (L-07 / NN-16).
 * Each Admin feature contributes its own nav items from its own file.
 */

export type NavGroup = 'home' | 'admissions' | 'people' | 'academics' | 'finance' | 'records' | 'settings';

export interface NavItem {
  key: string;
  label: string;
  /** Path relative to /i/:institution/admin — e.g. 'students' → /i/gw-senior/admin/students */
  path: string;
  group: NavGroup;
  order?: number;
  icon?: ReactNode;
  count?: number;
  /** Renders the count as hot/red — a signal that needs attention. */
  hot?: boolean;
  /** Reserved for permission-key filtering when auth lands (P-06). */
  requires?: string;
}

const items: NavItem[] = [];

export function registerAdminNavItem(item: NavItem): void {
  const dupe = items.findIndex((i) => i.key === item.key);
  if (dupe >= 0) items[dupe] = item;
  else items.push(item);
}

export const GROUP_ORDER: readonly NavGroup[] = [
  'home',
  'admissions',
  'people',
  'academics',
  'finance',
  'records',
  'settings',
];

export const GROUP_LABELS: Record<NavGroup, string> = {
  home: 'Home',
  admissions: 'Admissions',
  people: 'People',
  academics: 'Academics',
  finance: 'Finance',
  records: 'Records',
  settings: 'Settings',
};

/** Group items in fixed order, sorted within each group. */
export function groupedAdminNav(): Array<{ group: NavGroup; label: string; items: NavItem[] }> {
  return GROUP_ORDER.map((group) => ({
    group,
    label: GROUP_LABELS[group],
    items: items
      .filter((i) => i.group === group)
      .sort((a, b) => (a.order ?? 100) - (b.order ?? 100)),
  })).filter((section) => section.items.length > 0);
}
